// AboutPage - About page for ContentOS 3

import React from 'react';
import {
    Sparkles,
    Cpu,
    Layers,
    Zap,
    PenTool,
    Globe,
    Rocket,
    Mail,
    MapPin,
    MessageCircle,
    AlertTriangle,
    Scale,
    ShieldCheck,
} from 'lucide-react';

const AboutPage = ({ setShowRecharge }) => {
    return (
        <div className="w-full min-h-full bg-white p-10 animate-in fade-in duration-500">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-24 mt-10">
                    <div className="inline-block mb-6 p-4 rounded-[2rem] bg-white border border-blue-50 shadow-xl shadow-blue-100/50">
                        <div className="w-24 h-24 relative bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                            <span className="text-white font-bold text-5xl">C</span>
                        </div>
                    </div>
                    <h1 className="text-6xl font-black text-gray-900 tracking-tight mb-4">
                        Content<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">OS 3</span>
                    </h1>
                    <p className="text-xl text-gray-500 font-light max-w-2xl mx-auto leading-relaxed">
                        AI 驱动的多平台内容创作工具。<br />从热点追踪到内容生成，从排版美化到一键发布，让创作更高效。
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
                    <div className="bg-gray-50 rounded-[32px] p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                        <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                            <Rocket className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">我们的使命</h3>
                        <p className="text-gray-500 leading-relaxed">让每个创作者都能高效产出优质内容，用 AI 技术打破创作壁垒，释放无限想象力。</p>
                    </div>

                    <div className="md:col-span-2 bg-gradient-to-br from-gray-50 to-white rounded-[32px] p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-[80px] -mr-16 -mt-16 opacity-50"></div>
                        <h3 className="text-xl font-bold text-gray-900 mb-8 relative z-10">核心功能</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-8 gap-x-4 relative z-10">
                            <div className="flex flex-col gap-2">
                                <div className="p-2 bg-white rounded-lg w-fit shadow-sm border border-gray-100"><Cpu className="w-5 h-5 text-purple-500" /></div>
                                <span className="font-bold text-gray-900">AI 内容生成</span>
                                <span className="text-xs text-gray-500">多模型智能创作</span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="p-2 bg-white rounded-lg w-fit shadow-sm border border-gray-100"><Layers className="w-5 h-5 text-blue-500" /></div>
                                <span className="font-bold text-gray-900">多平台适配</span>
                                <span className="text-xs text-gray-500">小红书/公众号</span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="p-2 bg-white rounded-lg w-fit shadow-sm border border-gray-100"><Zap className="w-5 h-5 text-yellow-500" /></div>
                                <span className="font-bold text-gray-900">热点追踪</span>
                                <span className="text-xs text-gray-500">实时趋势洞察</span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="p-2 bg-white rounded-lg w-fit shadow-sm border border-gray-100"><PenTool className="w-5 h-5 text-green-500" /></div>
                                <span className="font-bold text-gray-900">智能排版</span>
                                <span className="text-xs text-gray-500">一键格式化输出</span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="p-2 bg-white rounded-lg w-fit shadow-sm border border-gray-100"><Globe className="w-5 h-5 text-cyan-500" /></div>
                                <span className="font-bold text-gray-900">RSS 订阅</span>
                                <span className="text-xs text-gray-500">内容灵感聚合</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative rounded-[40px] overflow-hidden bg-[#1e1b4b] text-white p-12 md:p-20 mb-24 text-center shadow-2xl shadow-blue-900/20">
                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-[120px] -translate-y-1/2"></div>
                        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-[120px] translate-y-1/2"></div>
                    </div>

                    <div className="relative z-10 flex flex-col items-center">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-8">
                            <Sparkles className="w-4 h-4 text-yellow-400 fill-current" />
                            <span className="text-sm font-bold tracking-wide text-white/90">开始创作之旅</span>
                        </div>

                        <h2 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
                            不仅是工具，<br />更是您的 <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">AI 创作助手</span>
                        </h2>

                        <p className="text-sm text-white/60 max-w-2xl mx-auto mb-12">
                            ContentOS 3 集成热点追踪、内容生成、智能排版于一体，帮助创作者高效产出优质内容。
                        </p>
                    </div>
                </div>

                {/* 法律声明区块 */}
                <div className="mb-16">
                    {/* 区块标题 */}
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                            <Scale className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">法律声明与用户须知</h3>
                            <p className="text-xs text-gray-400">请在使用前仔细阅读以下条款</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* 卡片 1：服务范围 */}
                        <div className="rounded-2xl bg-blue-50 border border-blue-100 p-6">
                            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                                <PenTool className="w-4 h-4 text-blue-600" />
                            </div>
                            <h4 className="font-bold text-gray-900 mb-2 text-sm">服务范围</h4>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                ContentOS 仅提供<strong className="text-gray-700">图文内容 AI 辅助创作工具</strong>，帮助用户生成文字、排版与内容灵感参考。平台不提供任何形式的传播渠道、账号运营或代发服务。
                            </p>
                        </div>

                        {/* 卡片 2：禁止行为 */}
                        <div className="rounded-2xl bg-red-50 border border-red-100 p-6">
                            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                            </div>
                            <h4 className="font-bold text-gray-900 mb-2 text-sm">禁止行为</h4>
                            <ul className="text-xs text-gray-500 leading-relaxed space-y-1">
                                <li>· 生成违反法律法规的内容</li>
                                <li>· 制造、传播虚假信息或谣言</li>
                                <li>· 侵犯他人知识产权、名誉权</li>
                                <li>· 生成含有色情、暴力、歧视的内容</li>
                                <li>· 利用平台从事任何欺诈活动</li>
                            </ul>
                        </div>

                        {/* 卡片 3：责任声明 */}
                        <div className="rounded-2xl bg-green-50 border border-green-100 p-6">
                            <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                                <ShieldCheck className="w-4 h-4 text-green-600" />
                            </div>
                            <h4 className="font-bold text-gray-900 mb-2 text-sm">责任声明</h4>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                AI 生成内容仅供参考，<strong className="text-gray-700">用户须对所创作及发布的内容承担全部法律责任</strong>。因用户违规使用导致的任何法律纠纷、经济损失或处罚，平台不承担连带责任。
                            </p>
                        </div>
                    </div>

                    {/* 底部醒目提示 */}
                    <div className="mt-4 flex items-start gap-3 px-5 py-4 rounded-2xl bg-amber-50 border border-amber-200">
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700 leading-relaxed">
                            <strong>重要提示：</strong>使用本平台生成的内容前，请确保其符合中华人民共和国相关法律法规及目标平台的社区规范。对于因违法使用本工具所产生的一切后果，用户须自行承担全部法律责任，与 ContentOS 平台无关。
                        </p>
                    </div>
                </div>

                <div className="text-center border-t border-gray-100 pt-12">
                    <h4 className="text-lg font-bold text-gray-900 mb-8">联系我们</h4>
                    <div className="flex justify-center gap-12 text-sm text-gray-500">
                        <div className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer">
                            <Mail className="w-4 h-4" />
                            aiyunchuang@yfqctech.cn
                        </div>
                        <div className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer">
                            <MapPin className="w-4 h-4" />
                            中国·成都
                        </div>
                        <div className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer">
                            <MessageCircle className="w-4 h-4" />
                            微信：YFQC_ai
                        </div>
                    </div>
                    <div className="mt-12 pb-8 flex flex-col items-center gap-2">
                        <p className="text-xs text-gray-400">Copyright &copy; 2026 ContentOS. All rights reserved.</p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AboutPage;
